import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  resolveStoreOnboardingState,
  type StoreAccessRole,
} from "@/lib/storeOnboarding";

type Role = "admin" | "manager" | "cashier";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  roles: Role[];
  storeAccessRoles: StoreAccessRole[];
  // The exact user id `roles`/`storeAccessRoles` were resolved for, or
  // null while unresolved/clearing. Consumers that need an extra,
  // explicit guarantee that authorization data belongs to the CURRENT
  // user (beyond trusting `loading`) can compare this to `user?.id`
  // themselves -- see useStoreScopedManagerAccess.
  authorizationUserId: string | null;
  hasStoreAccess: boolean;
  canCreateFirstStore: boolean;
  shouldAskAdminForStoreAccess: boolean;
  storeOnboardingStatus: ReturnType<typeof resolveStoreOnboardingState>["status"];
  isAdmin: boolean;
  isManager: boolean;
  // True only for the global `admin` role in `public.user_roles`
  // (mirrors `public.has_role(uid, 'admin')` exactly) -- unlike `isAdmin`,
  // this does NOT include a direct-store owner/admin role at any single
  // store. Introduced for F-05A pricebook containment: server-side RLS
  // and RPCs for global pricebook mutations now require the equivalent of
  // this flag, so any frontend gate for those specific actions must use
  // this, not `isAdmin`, or the UI will show enabled controls that the
  // server then rejects. See docs/09-execution/F-05A-Pricebook-Containment.md.
  isGlobalAdmin: boolean;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  roles: [],
  storeAccessRoles: [],
  authorizationUserId: null,
  hasStoreAccess: false,
  canCreateFirstStore: false,
  shouldAskAdminForStoreAccess: false,
  storeOnboardingStatus: "needs_store_assignment",
  isAdmin: false,
  isManager: false,
  isGlobalAdmin: false,
  loading: true,
  refreshAuth: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [storeAccessRoles, setStoreAccessRoles] = useState<StoreAccessRole[]>([]);
  // Which user id `roles`/`storeAccessRoles` currently belong to (or were
  // last resolved for). Cleared to null the instant the authenticated
  // user changes, before the new user's own roles have even started
  // resolving.
  const [authorizationUserId, setAuthorizationUserId] = useState<string | null>(null);
  // True from the moment a user becomes current until THAT user's own
  // roles/storeAccessRoles have resolved (or resolved to "no user").
  // Combined with sessionLoading below to form the public `loading` flag
  // -- `loading` therefore means "authorization for the CURRENT user is
  // not yet confirmed", not merely "the very first session fetch hasn't
  // finished", so it correctly re-arms on every user switch, not just on
  // initial app load.
  const [authorizationResolving, setAuthorizationResolving] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(true);

  // Monotonically increasing generation counter. Every user change (sign
  // in, sign out, switch) bumps it immediately and synchronously, which
  // invalidates any authorization fetch already in flight for a previous
  // user: that fetch captured the generation value current when IT
  // started, and checks it against the live ref before applying its
  // result -- if a newer generation has since started, the stale result
  // is dropped instead of silently overwriting the current user's state.
  // A ref (not state) because refreshAuth and the auth-state-change
  // handler both need to read/bump the same counter outside of React's
  // render cycle.
  const generationRef = useRef(0);

  const clearAuthorization = () => {
    setRoles([]);
    setStoreAccessRoles([]);
    setAuthorizationUserId(null);
  };

  const loadAuthorizationFor = async (uid: string) => {
    generationRef.current += 1;
    const myGeneration = generationRef.current;
    setAuthorizationResolving(true);
    const [rolesRes, accessRes] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", uid),
      supabase.from("user_store_access").select("role").eq("user_id", uid),
    ]);
    // A newer generation (another user change, or a concurrent refresh)
    // has started since this fetch began -- its own load owns the final
    // state now, so this stale response must not apply.
    if (myGeneration !== generationRef.current) return;
    setRoles(((rolesRes.data ?? []) as Array<{ role: string }>).map((r) => r.role as Role));
    setStoreAccessRoles(
      ((accessRes.data ?? []) as Array<{ role: string }>).map((r) => r.role as StoreAccessRole),
    );
    setAuthorizationUserId(uid);
    setAuthorizationResolving(false);
  };

  useEffect(() => {
    // Listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Invalidate any authorization fetch already in flight for whoever
      // was current before this event, and fail closed immediately --
      // before the new user's own roles have even started resolving, not
      // after.
      generationRef.current += 1;
      clearAuthorization();
      // F-13: any cached React Query data belongs to the PREVIOUS user
      // and must never render for the newly authenticated user. Clear on
      // sign-out and on user switch (session for a different uid).
      const prevUid = user?.id ?? null;
      const nextUid = s?.user?.id ?? null;
      if (event === "SIGNED_OUT" || prevUid !== nextUid) {
        queryClient.clear();
      }
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        setAuthorizationResolving(true);
        const uid = s.user.id;
        // Defer DB call to avoid deadlock inside the callback
        setTimeout(() => { void loadAuthorizationFor(uid); }, 0);
      } else {
        // Signed out: nothing to resolve -- stay cleared and resolved.
        setAuthorizationResolving(false);
      }
    });

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      generationRef.current += 1;
      clearAuthorization();
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        setAuthorizationResolving(true);
        await loadAuthorizationFor(s.user.id);
      } else {
        setAuthorizationResolving(false);
      }
      setSessionLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const refreshAuth = async () => {
    // Reads the live `user` via closure each call; loadAuthorizationFor's
    // own generation guard protects this exactly like any other fetch --
    // if the user changes again before this resolves, the stale result
    // is dropped the same way.
    if (user) await loadAuthorizationFor(user.id);
  };

  const signOut = async () => {
    // F-13: clear cached queries eagerly so the auth-state callback that
    // fires next never has a window to render prior-user data.
    queryClient.clear();
    await supabase.auth.signOut();
  };

  // Only ever trust roles/storeAccessRoles that are confirmed to belong
  // to the current user -- authorizationUserId is set (by
  // loadAuthorizationFor) only after passing the generation check above,
  // so this is a second, independent guarantee beyond `loading` alone.
  const resolvedRoles = authorizationUserId === (user?.id ?? null) ? roles : [];
  const resolvedStoreAccessRoles = authorizationUserId === (user?.id ?? null) ? storeAccessRoles : [];

  const onboarding = resolveStoreOnboardingState({ user, roles: resolvedRoles, storeAccessRoles: resolvedStoreAccessRoles });
  const hasStoreSetupRole = onboarding.setupRoles.length > 0;
  const isAdmin = resolvedRoles.includes("admin") || onboarding.setupRoles.includes("owner") || onboarding.setupRoles.includes("admin");
  const isManager = isAdmin || resolvedRoles.includes("manager") || hasStoreSetupRole;
  const isGlobalAdmin = resolvedRoles.includes("admin");
  const loading = sessionLoading || authorizationResolving;

  return (
    <Ctx.Provider
      value={{
        user,
        session,
        roles: resolvedRoles,
        storeAccessRoles: resolvedStoreAccessRoles,
        authorizationUserId,
        hasStoreAccess: onboarding.hasStoreAccess,
        canCreateFirstStore: onboarding.canCreateFirstStore,
        shouldAskAdminForStoreAccess: onboarding.shouldAskAdminForStoreAccess,
        storeOnboardingStatus: onboarding.status,
        isAdmin,
        isManager,
        isGlobalAdmin,
        loading,
        refreshAuth,
        signOut,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
